import axios from "axios";
import { forwardRef, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { RiArrowDownSLine } from "react-icons/ri";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { FaSearch } from "react-icons/fa";
import defaultImage from "../../../utils/DefaultImage";
import PaginationComponent from "../../../utils/PaginationComponent";
import TableLoader from "../../../utils/TableLoader";

const StockManage = (props, ref) => {
  const { hasPermission } = props;
  const { rest_slug2 } = useParams();
  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("Role");

  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stockId, setStockId] = useState(null);
  const [stockData, setStockData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchList, setSearchList] = useState([]);
  const [showSearchList, setShowSearchList] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [count, setCount] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [priceOptions, setPriceOptions] = useState([]);

  // Refs
  const actionRef = useRef(null);
  const reasonRef = useRef(null);
  const priceRef = useRef(null);
  const searchContainerRef = useRef(null);

  const [selectedWineId, setSelectedWineId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownOpen2, setIsDropdownOpen2] = useState(false);
  const [isDropdownOpen3, setIsDropdownOpen3] = useState(false);

  // Toggle dropdown visibility
  const toggleDropdown = () => { setIsDropdownOpen((prevState) => !prevState) };
  const toggleDropdown2 = () => { setIsDropdownOpen2((prevState) => !prevState) };
  const toggleDropdown3 = () => { setIsDropdownOpen3((prevState) => !prevState) };

  // Action and reason options
  const actions = [
    { value: "increase", label: "Aumenta Stock" },
    { value: "decrease", label: "Diminuisci Stock" },
  ];
  const IncreaseOptions = ["Nuovo ordine", "Omaggio", "Inventario"];
  const DecreaseOptions = [
    "Vendita",
    "Vendita al bicchiere",
    "Rottura",
    "Autoconsumo",
    "Inventario",
  ];

  // Determine reason options based on selected action
  const reasonOptions =
    selectedAction === "increase"
      ? IncreaseOptions
      : selectedAction === "decrease"
        ? DecreaseOptions
        : ["No options"];

  // Select action handler
  const handleActionSelect = (action) => {
    setSelectedAction(action);
    setSelectedReason(null);
    setIsDropdownOpen(false);
    setCount(0);
  };

  // Select reason handler
  const handleReasonSelect = (reason) => {
    setSelectedReason(reason);
    setIsDropdownOpen2(false);
  };
  // Select winePrice handler
  const handleWinePriceSelect = (reason) => {
    setSelectedPrice(reason);
    setIsDropdownOpen3(false);
  };

  const searchStockData = async (textToSearch) => {
    try {
      let response;
      if (userRole === "admin") {
        // Admin API Call (/api/v1/wines)
        response = await axios.get(`${apiUrl}/api/v1/wines`, {
          params: {
            pageNumber: 1,
            size: 10,
            search: textToSearch || "",
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        // Restaurant API Call (/api/v1/wine-stock/{rest_slug2})
        response = await axios.get(
          `${apiUrl}/api/v1/wine-stock/${rest_slug2}`,
          {
            params: {
              pageNumber: 1,
              size: 10,
              search: textToSearch || "",
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      setSearchList(response?.data?.data);
      setShowSearchList(true);
    } catch (error) {
      console.error("Error fetching search data:", error);
    }
  };

  const handleWineSelect = async (wine) => {
    setSelectedWineId(wine?.id);
    setStockId(wine?.id);
    setPriceOptions(wine?.meta || []);
    if (userRole === "admin") {
      try {
        const response = await axios.get(`${apiUrl}/api/v1/restaurants/${rest_slug2}/wine/${wine?.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = response.data;
        setSearchText(wine?.name || "");
        setQuantity(data?.bottles_quantity || 0);
      } catch (error) {
        console.error("Error fetching wine details:", error);
        setSearchText(wine?.name || "");
        setQuantity(0);
      }
    } else {
      setSearchText(wine?.wine?.name || "");
      setQuantity(wine?.bottles_quantity || 0);
    }
    setSearchList([]);
    setShowSearchList(false);
    setSelectedAction(null);
    setSelectedReason(null);
    setCount(0);
  };

  const putStockData = async () => {
    setIsLoading(true);
    try {
      let apiEndpoint;
      let response;
      if (userRole === "admin") {
        apiEndpoint = `${apiUrl}/api/v1/restaurants/${rest_slug2}/wine-stock/manually-add-manage`;
        response = await axios.post(
          apiEndpoint,
          {
            quantity: count,
            reason: selectedReason,
            action: selectedAction,
            wine_id: selectedWineId,
            price: selectedPrice,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        apiEndpoint = `${apiUrl}/api/v1/wine-stock/${stockId}/update-quantity`;
        response = await axios.put(
          apiEndpoint,
          {
            change_in_quantity: count,
            reason: selectedReason,
            action: selectedAction,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      getStockHistoryData();
      setStockId(null);
      setSelectedWineId(null);
      setSearchText("");
      setSearchList([]);
      setSelectedAction(null);
      setSelectedReason(null);
      setCount(0);
      setQuantity(0);
      setSelectedPrice(null);
      toast.info(response.data.message || "Stock Updated Successfully");
    } catch (error) {
      console.error("Error updating stock data:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
      }
      toast.error("Error updating stock. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getStockHistoryData = async () => {
    setIsLoading(true);
    try {
      const params = {
        pageNumber: page,
        size: rowsPerPage,
      };
      const response = await axios.get(
        `${apiUrl}/api/v1/restaurants/${rest_slug2}/history`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = response.data;
      setStockData(data.data);
      setQuantity(0);
      setTotalPages(data.total);
      setCurrentPage(data.current_page);
    } catch (error) {
      console.error("Error fetching order data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickOutside = (event) => {
    if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
      setShowSearchList(false);
    }
  };

  useEffect(() => {
    if (searchText.trim() === "") {
      setSearchList([]);
      setShowSearchList(false);
      setSelectedAction(null);
      setSelectedReason(null);
      setCount(0);
      setQuantity(0);
      setSelectedPrice(null);
      return;
    }
    searchStockData();
  }, []);

  useEffect(() => {
    getStockHistoryData();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (!searchText || !selectedAction) {
      setCount(0);
    }
  }, [searchText, selectedAction]);

  useEffect(() => {
    if (userRole === "admin" && searchText.trim() !== "" && priceOptions?.length > 0) {
      const highestPrice = Math.max(...priceOptions.map((option) => parseFloat(option?.b2b_price) || 0));
      setSelectedPrice(String(highestPrice));
    } else if (searchText.trim() !== "") {
      setSelectedPrice(0.0);
    }
  }, [priceOptions]);


  // useEffect(() => {
  //   if (userRole === "admin" && searchText.trim() !== "" && priceOptions?.length > 0) {
  //     const highestPrice = Math.max(...priceOptions.map((option) => option?.b2b_price || 0));
  //     setSelectedPrice(highestPrice);
  //   } else if (searchText.trim() !== "") {
  //     setSelectedPrice(0.00);
  //   }
  // }, [priceOptions]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionRef.current && !actionRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (reasonRef.current && !reasonRef.current.contains(event.target)) {
        setIsDropdownOpen2(false);
      }
      if (priceRef.current && !priceRef.current.contains(event.target)) {
        setIsDropdownOpen3(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handlePageRefresh = () => {
      let searchTexts = JSON.parse(localStorage.getItem("searchTexts")) || {};
      delete searchTexts["Stock Management Text"];
      localStorage.setItem("searchTexts", JSON.stringify(searchTexts));
    };

    window.addEventListener("beforeunload", handlePageRefresh);

    return () => {
      window.removeEventListener("beforeunload", handlePageRefresh);
    };
  }, []);

  return (
    <>
      <section className="relative">
        <h3 className="text-2xl text-left font-bold mb-2 poppins-medium">
          Gestisci Cantina
        </h3>
        <div className="w-full flex items-center my-5 justify-center flex-wrap">
          <div className="w-full gap-[20px]">
            {/* Search Input */}
            <div className="relative group" ref={searchContainerRef}>
              <div className="inline-flex h-full justify-between items-center w-full px-[23px] pl-[23px] sm:py-[15px] py-[10px] outline-none text-[#8F8F8F] border border-[#D9D9D9] rounded-full shadow-sm">
                <input
                  type="text"
                  placeholder="Cerca vino"
                  className="text-[#8F8F8F] sm:text-[16px] text-[13px] outline-none poppins-medium relative w-full h-full"
                  value={searchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStockId(null);
                    if (value.length === 1 && value.startsWith(" ")) return;
                    setSearchText(value);
                    searchStockData(value);
                    let searchTexts = JSON.parse(localStorage.getItem("searchTexts")) || {};
                    searchTexts["Stock Management Text"] = value;
                    localStorage.setItem("searchTexts", JSON.stringify(searchTexts));
                  }}
                />
                <FaSearch />
              </div>
              {/* Display search results */}
              {showSearchList && searchText && (
                <div className="absolute mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-full z-[99]">
                  {searchList.length > 0 ? (
                    searchList.map((wine, index) => (
                      <div
                        key={index}
                        onClick={() => handleWineSelect(wine)}
                        className="cursor-pointer p-2 hover:bg-gray-100"
                      >
                        {localStorage.getItem("Role") === "admin"
                          ? wine?.name
                          : wine?.wine?.name}
                      </div>
                    ))
                  ) : (
                    <p className="p-2 text-gray-500">No results found</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-full flex items-center my-5 justify-center flex-wrap">
          <div className="w-full grid md:grid-cols-2  gap-4 ">
            {/* Action Dropdown */}
            <div className="dropdown-main mb-3" ref={actionRef}>
              <div className="relative group w-full bg-[#FFFFFF] dropdown-container">
                <div className="border border-gray-300 rounded-3xl">
                  <div
                    onClick={() => toggleDropdown()}
                    className="flex justify-between poppins-medium items-center px-4 sm:py-[20px] py-[10px] sm:text-sm text-[13px] text-[#8B8B8B] rounded-3xl bg-transparent cursor-pointer relative z-10"
                  >
                    {selectedAction && searchText
                      ? actions.find(
                        (action) => action.value === selectedAction
                      )?.label
                      : "Seleziona Azione"}
                    <RiArrowDownSLine
                      className={`transform transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : ""
                        }`}
                      size={"24px"}
                      color="#9f414f"
                    />
                  </div>
                  {isDropdownOpen && (
                    <div className="z-[99] max-h-60 bg-[#FFFFFF] absolute sm:top-14 top-[32px] p-2 px-4 mt-3 rounded-[21px] shadow-lg text-[#DB7685] space-y-1 w-full transition-all duration-300 ease-in-out">
                      {searchText ? (
                        <>
                          {actions.map(
                            (action, index) =>
                              ((hasPermission("WineStock", "add") &&
                                action.value === "increase") ||
                                (hasPermission("WineStock", "remove") &&
                                  action.value === "decrease")) && (
                                <label
                                  key={action.value}
                                  className={`${index !== actions.length - 1
                                    ? "border-b border-[#8b24331f]"
                                    : ""
                                    } px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn`}
                                  onClick={() =>
                                    handleActionSelect(action.value)
                                  }
                                >
                                  <span className="text-xs text-[#8B8B8B] rounded-md flex items-center gap-3 poppins-regular">
                                    {action.label}
                                  </span>
                                </label>
                              )
                          )}{" "}
                        </>
                      ) : (
                        <>
                          <label className=" border-[#8b24331f] px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn">
                            <span className="text-xs text-[#8B8B8B] rounded-md flex items-center gap-3 poppins-regular">
                              No options
                            </span>
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Reason Dropdown */}
            <div className="dropdown-main" ref={reasonRef}>
              <div className="relative group w-full bg-[#FFFFFF] dropdown-container">
                <div className="border border-gray-300 rounded-3xl">
                  <div
                    onClick={() => toggleDropdown2()}
                    className=" flex justify-between poppins-medium items-center px-4 sm:py-[20px] py-[10px] sm:text-sm text-[13px] text-[#8B8B8B] rounded-3xl bg-transparent cursor-pointer relative z-10"
                  >
                    {(searchText && selectedReason) || "Seleziona Causale"}
                    <RiArrowDownSLine
                      className={`transform transition-transform duration-300 ${isDropdownOpen2 ? "rotate-180" : ""
                        }`}
                      size={"24px"}
                      color="#9f414f"
                    />
                  </div>
                  {isDropdownOpen2 && (
                    <div className="z-[99] max-h-60 bg-[#FFFFFF] absolute sm:top-14 top-[30px] p-2 px-4 mt-3 rounded-[21px] shadow-lg text-[#DB7685] space-y-1 w-full transition-all duration-300 ease-in-out">
                      {searchText ? (
                        <>
                          {reasonOptions?.map((reason, index) => (
                            <label
                              key={reason}
                              className={`${index !== reasonOptions.length - 1
                                ? "border-b border-[#8b24331f]"
                                : ""
                                } px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn`}
                              onClick={() => handleReasonSelect(reason)}
                            >
                              <span className="text-xs text-[#8B8B8B] rounded-md flex items-center gap-3 poppins-regular">
                                {reason}
                              </span>
                            </label>
                          ))}
                        </>
                      ) : (
                        <>
                          <label className=" border-[#8b24331f] px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn">
                            <span className="text-xs text-[#8B8B8B] rounded-md flex items-center gap-3 poppins-regular">
                              No options
                            </span>
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-[20px] items-center justify-start">
          <div className="flex w-[162px] items-center h-[56px] border-[1px] px-[24px] py-[15px] rounded-[28px] border-gray-border justify-between">
            <button onClick={() => count > 1 && setCount(count - 1)} disabled={!searchText || !selectedAction}>
              <FaMinus className={!searchText || !selectedAction ? "cursor-not-allowed" : ""} />
            </button>
            <div className="w-full flex justify-center">
              <input
                type="text"
                className="w-full text-center outline-none p-2 bg-transparent border-none"
                value={count}
                readOnly={!(searchText && selectedAction)}
                style={!(searchText && selectedAction) ? { cursor: "not-allowed" } : {}}
                onChange={(e) => {
                  const value = e.target.value;
                  if ((/^\d*\.?\d{0,2}$/.test(value) || value === "") && value.length <= 3) {
                    setCount(value === "" ? "" : parseFloat(value));
                  }
                }}
              />
            </div>
            <button
              onClick={() => {
                const numericCount = Number(count) || 0;
                if (selectedAction === "decrease" && quantity > numericCount) {
                  setCount(numericCount + 1);
                } else if (selectedAction === "increase") {
                  setCount(numericCount + 1);
                }
              }}
              className="cursor-pointer"
            >
              <FaPlus className={!searchText || !selectedAction ? "cursor-not-allowed" : ""} />
            </button>
          </div>
          {selectedWineId && (
            <p className="poppins-regular font-normal text-[#8F8F8F]">
              Quantità Disponibile: {quantity && searchText ? quantity : "0"}
            </p>
          )}
          {/* Wine Price Dropdown */}
          {userRole === "admin" && selectedWineId && (
            <div className="dropdown-main" ref={priceRef}>
              <div className="relative group w-full bg-[#FFFFFF] dropdown-container">
                <div className="border border-gray-300 rounded-full">
                  <div
                    onClick={() => toggleDropdown3()}
                    className="flex justify-between poppins-medium items-center px-4 sm:py-[15px] py-[10px] sm:text-sm text-[13px] text-[#8B8B8B] bg-transparent cursor-pointer relative z-10"
                  >
                    {selectedPrice && searchText ? selectedPrice : "Seleziona Prezzo"}
                    <RiArrowDownSLine className={`transform transition-transform ml-2 duration-300 ${isDropdownOpen3 ? "rotate-180" : ""}`} size={"24px"} color="#9f414f" />
                  </div>
                  {isDropdownOpen3 && (
                    <div className="z-[99] max-h-60 bg-[#FFFFFF] absolute sm:top-14 top-[30px] p-2 px-4 mt-3 rounded-3xl shadow-lg text-[#DB7685] space-y-1 w-full transition-all duration-300 ease-in-out">
                      {selectedWineId ? (
                        <>
                          {priceOptions
                            ?.sort((a, b) => b.b2b_price - a.b2b_price)
                            .map((price, index) => (
                              <label
                                key={index}
                                className={`${index !== priceOptions.length - 1
                                  ? "border-b border-[#8b24331f]"
                                  : ""
                                  } px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn`}
                                onClick={() =>
                                  handleWinePriceSelect(price?.b2b_price)
                                }
                              >
                                <span className="text-xs text-[#8B8B8B] rounded-md flex items-center gap-3 poppins-regular">
                                  {price?.b2b_price}
                                </span>
                              </label>
                            ))}
                        </>
                      ) : (
                        <>
                          <label className=" border-[#8b24331f] px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn">
                            <span className="text-xs text-[#8B8B8B] rounded-md flex items-center gap-3 poppins-regular">
                              No options
                            </span>
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className=" flex items-center justify-start mt-[34px]">
          <button
            disabled={stockId === null || !selectedReason || count === 0}
            className={`sm:w-fit w-full flex justify-center py-2 px-2 items-center text-sm ${stockId !== null && selectedReason && count > 0
              ? "hover:text-[#8A2533] hover:bg-[#f6efef] bg-[#8A2533] border-2 border-[#953543] text-white"
              : "bg-[#bc41513f] text-[#b22d2d] cursor-not-allowed"
              } rounded-lg transition-colors group`}
            onClick={() => {
              if (stockId !== null && selectedReason && count > 0) {
                putStockData();
              }
            }}
          >
            <span className="poppins-regular text-sm">Aggiorna Stock</span>
          </button>
        </div>
      </section>
      <section>
        <h3 className="text-2xl font-bold mt-10 mb-2 poppins-medium">
          Storico Movimenti
        </h3>
        <div className="wine-main-stock w-auto h-auto bg-[#F8F8F8] border-[1.5px] border-[#8B24332B] text-[#303030] pt-3 pb-0 px-6 rounded-xl shadow">
          <table className="w-full text-left ristoranti_table">
            <thead>
              <tr className="border-b ">
                <th className="pb-3 pr-2 text-left poppins-medium">Sr.no</th>
                <th className="pb-3 pr-2 text-left poppins-medium">Immagine</th>
                <th className="pb-3 px-2 text-left poppins-medium">Nome</th>
                <th className="pb-3 px-2 text-left poppins-medium">Azione</th>
                <th className="pb-3 px-2 text-left poppins-medium">
                  Quantità movimentate
                </th>
                <th className="pb-3 px-2 text-left poppins-medium">Causale</th>
                <th className="pb-3 px-2 text-left poppins-medium">Data</th>
              </tr>
            </thead>
            {isLoading ? (
              <TableLoader />
            ) : (
              <tbody>
                {stockData?.length ? (
                  stockData?.map((stockData, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-4 px-2 text-left poppins-regular">
                        {(index + 1 + (currentPage - 1) * rowsPerPage).toString().padStart(2, "0")}
                      </td>
                      <td className="py-4 text-left poppins-regular">
                        <img
                          src={
                            stockData?.wine_stock?.wine?.image ||
                            defaultImage
                          }
                          alt={
                            stockData?.wine_stock?.wine?.name || "Wine image"
                          }
                          className="w-12 bg-white h-12 border border-[#68172394] rounded-lg object-contain"
                        />
                      </td>
                      <td className="py-4 px-2 text-left poppins-regular">
                        {stockData?.wine_stock?.wine?.name || (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="py-4 px-2 text-left poppins-regular">
                        {stockData?.action === "increase" ? (
                          "Aumento"
                        ) : stockData?.action === "decrease" ? (
                          "Diminuire"
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="py-4 px-2 text-left poppins-regular flex justify-center">
                        <span className="flex w-[32px] h-[32px] self-center items-center justify-center bg-[#8B2433A6] text-white rounded-full">
                          {stockData?.change_in_quantity || (
                            <span className="text-gray-200">0</span>
                          )}
                        </span>
                      </td>
                      <td className="py-4 px-2 text-left poppins-regular">
                        {stockData?.reason || (
                          <span className="text-gray-400">
                            No reason provided
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-left poppins-regular">
                        {stockData?.created_at ? (
                          new Date(stockData.created_at)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-") // Replace slashes with dashes
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="py-8 text-center text-gray-500 poppins-regular"
                    >
                      No stock data available
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          </table>
        </div>
        {stockData && stockData?.length > 0 && (
          <PaginationComponent
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </section>
    </>
  );
};

export default forwardRef(StockManage);
